import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, Typography, Avatar, IconButton, TextField, Paper } from "@mui/material";
import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import SendIcon from "@mui/icons-material/Send";
import { capitalizerFirstLetter } from "../../utils";
import { useAuthStore } from "../../store/useAuthStore";

export const CommentsSheet = ({ isOpen, setIsOpen, comments, sendComment }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [comment, setComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [maxHeight, setMaxHeight] = useState("40vh");
    const creator = useAuthStore((state) => state.creator);

    const hasUserAlreadyCommented = useMemo(
        () => creator?.id && comments.some((c) => c.creator?.id === creator.id),
        [creator, comments],
    );

    const bottomSheetRef = useRef();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleSendComment = async () => {
        if (!comment.trim() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await sendComment(comment);
            setComment("");
        } finally {
            setIsSubmitting(false);
        }
    };

    const commentFormSection = hasUserAlreadyCommented ? (
        <Typography
            variant="body2"
            sx={{
                color: "var(--grey-600-color)",
                textAlign: "center",
                fontStyle: "italic",
                width: "100%",
                py: 1,
            }}
        >
            Você já comentou neste post. Não é permitido adicionar mais de um comentário.
        </Typography>
    ) : (
        <Box
            component="form"
            onSubmit={(e) => {
                e.preventDefault();
                handleSendComment();
            }}
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
            }}
        >
            <TextField
                placeholder="Adicionar um comentário..."
                size="small"
                fullWidth
                variant="outlined"
                sx={{
                    mr: 1,
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-input": {
                        padding: "10px 14px",
                    },
                }}
                onChange={(e) => setComment(e.target.value)}
                value={comment}
            />
            <IconButton
                color="primary"
                onClick={handleSendComment}
                disabled={!comment.trim() || isSubmitting}
                sx={{
                    bgcolor: comment.trim() ? "var(--primary-color)" : "var(--grey-200-color)",
                    color: "var(--white-color)",
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    "&:hover": {
                        bgcolor: comment.trim() ? "var(--primary-dark-color)" : "var(--grey-200-color)",
                    },
                    "&.Mui-disabled": {
                        bgcolor: "var(--grey-200-color)",
                        color: "var(--grey-400-color)",
                    },
                }}
            >
                <SendIcon fontSize="small" />
            </IconButton>
        </Box>
    );

    const renderComments = () => (
        <Box>
            <Paper
                elevation={3}
                sx={{
                    backgroundColor: "var(--white-color)",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: maxHeight,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                }}
            >
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: "var(--title-font)",
                            fontWeight: 600,
                            color: "var(--grey-800-color)",
                        }}
                    >
                        Comentários
                    </Typography>
                </Box>

                <Box
                    sx={{
                        overflowY: "auto",
                        flex: 1,
                        px: { xs: 2, sm: 3 },
                        pb: 2,
                        "&::-webkit-scrollbar": {
                            width: "6px",
                            backgroundColor: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "var(--grey-200-color)",
                            borderRadius: "6px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "var(--grey-300-color)",
                        },
                        "scrollbarWidth": "thin",
                        "scrollbarColor": "var(--grey-200-color) transparent",
                    }}
                >
                    {comments.map((comment, index) => {
                        const isLastComment = index === comments.length - 1;
                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    mb: 2.5,
                                    pb: 2,
                                    borderBottom: isLastComment ? "none" : "1px solid var(--grey-50-color)",
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: 14,
                                        mt: 0.5,
                                        bgcolor: "var(--primary-color)",
                                        color: "var(--white-color)",
                                    }}
                                >
                                    {comment.creator?.nome?.charAt(0) ?? "A"}
                                </Avatar>

                                <Box sx={{ ml: 1.5, width: "100%" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 0.5,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                lineHeight: 1.2,
                                                fontWeight: 600,
                                                color: "var(--grey-800-color)",
                                                mr: 1,
                                            }}
                                        >
                                            {comment.creator?.nome || "Anônimo"}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "var(--grey-400-color)",
                                                fontSize: "0.7rem",
                                            }}
                                        >
                                            {new Date(comment?.createdAt).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 0.5,
                                            color: "var(--grey-700-color)",
                                            lineHeight: 1.4,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {capitalizerFirstLetter(comment.texto)}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                <Box
                    sx={{
                        py: 3,
                        px: 2,
                        borderTop: "1px solid var(--grey-100-color)",
                        bgcolor: "var(--white-color)",
                    }}
                >
                    {commentFormSection}
                </Box>
            </Paper>
        </Box>
    );

    const sheetStyle = {
        overflowY: "auto",
        backgroundColor: "transparent",
        position: "relative",
        maxWidth: "480px",
        margin: "0 auto",
        zIndex: 1300,
    };

    return isMobile ? (
        <BottomSheet
            open={isOpen}
            onDismiss={() => setIsOpen(false)}
            ref={bottomSheetRef}
            snapPoints={({ maxHeight }) => [0.6 * maxHeight, maxHeight]}
            defaultSnap={({ maxHeight }) => 0.6 * maxHeight}
            onSpringEnd={(event) => {
                if (event.type === "SNAP" && event.source === "drag") {
                    setMaxHeight(event.to === 1 ? "80vh" : "40vh");
                }
            }}
            style={sheetStyle}
        >
            {renderComments()}
        </BottomSheet>
    ) : (
        <>{renderComments()}</>
    );
};
